import { api } from "aldoo-ra/API"
//construct the endpoint
const storeAPI = api("store")
//a list of preprocessing actions that can modify the payment data before it's being sent
const paymentPreprocessActions = {}

//process the payment data by applying the user custom action modifications
const preprocessPaymentData = paymentData => {
    //for all defined paymentPreprocessActions
    for (const action of Object.values(paymentPreprocessActions)) {
        if (!action) continue
        paymentData = action(paymentData)
    }
    return paymentData
}
/**
 * Add a payment preprocess with id, action
 * @param {*} param0
 */
export const addPaymentPreprocessor = ({ id, action }) => {
    paymentPreprocessActions[id] = action
}

/**
 * Remove a preprocessor action by id
 * @param {*} id
 */
export const removePaymentPreprocessor = id => {
    delete paymentPreprocessActions[id]
}

//Initiate a pay for a product
export const initializePayment = async (payload) => {
    //define the payment data and apply user preprocessors
    //Example: some tracking library needs to add extra data to the payload
    const initiate_payment = preprocessPaymentData({
        platform: "Web",
        ...payload
    })
    //send the actual payment data
    return await storeAPI({ initiate_payment })
}

export const completeSubscriptionAfterSetup = async (payload) => {
    //apply payment preprocessors (e.g., Impact click ID)
    const complete_subscription_after_setup = preprocessPaymentData({
        platform: "Web",
        ...payload
    })
    return await storeAPI({ complete_subscription_after_setup })
}

export const attachPaymentMethod = async ({
    payment_method_id,
    makeDefault,
}) => {
    return await storeAPI({
        attach_payment_method: {
            platform: "Web",
            payment_method_id,
            makeDefault,
        },
    })
}

export const detachPaymentMethod = async (paymentMethodId) => {
    return await storeAPI({
        detach_payment_method: {
            platform: "Web",
            paymentMethodId,
        },
    })
}

//get payment methods for the user
export const getPaymentMethods = async () => {
    return await storeAPI({
        get_payment_methods: {
            platform: "Web",
        },
    })
}

export const initializePaymentMethodSetup = async () => {
    return await storeAPI({
        initialize_payment_method_setup: {
            platform: "Web"
        }
    });
};

export const getPromoCodeInfo = async (promo_code) => {
    return await storeAPI({
        get_promo_code_info: {
            platform: "Web",
            promo_code: promo_code,
        },
    })
}

export const cancelIncompleteSubscription = async (subscriptionId) => {
    return await storeAPI({
        cancel_incomplete_subscription: {
            platform: "Web",
            subscriptionId,
        },
    })
}

export const cancelSubscription = async (subscriptionId) => {
    return await storeAPI({
        cancel_subscription: {
            platform: "Web",
            subscriptionId,
        },
    })
}

/**
 * Change the seat count of a per-seat subscription. Increases charge the
 * prorated difference immediately; decreases apply from the next period.
 */
export const updateSubscriptionQuantity = async ({ subscriptionId, quantity }) => {
    return await storeAPI({
        update_subscription_quantity: {
            platform: "Web",
            subscriptionId,
            quantity,
        },
    })
}

export const submitUnsubscribeFeedback = async ({ subscription_id, product_id, category, user_notes }) => {
    return await storeAPI({
        "unsubscribe-feedback": {
            subscription_id,
            product_id,
            category,
            user_notes,
        },
    })
}

export const getInvoices = async ({ page, itemsPerPage, search }) => {
    return await storeAPI({
        get_invoices: {
            platform: "Web",
            page,
            itemsPerPage,
            search
        }
    });
}

export const getStoreAccount = async () => {
    return await storeAPI({ account: true })
}

export const updateCustomerInvoiceDetails = async (payload) => {
    return await storeAPI({
        update_customer_invoice_details: {
            platform: "Web",
            ...payload
        }
    })
}

export const validateTaxId = async ({ taxId, country }) => {
    return await storeAPI({
        validate_tax_id: {
            taxId,
            country
        }
    })
}

export const getBillingDetails = async () => {
    return await storeAPI({
        get_billing_details: {
            platform: "Web"
        }
    })
}

export const updateBillingDetails = async ({ companyName, taxId, address, name }) => {
    return await storeAPI({
        update_billing_details: {
            platform: "Web",
            companyName,
            taxId,
            address,
            name
        }
    })
}

export const checkPaymentProcessing = async (payment_intent_id, subscription_id) => {
    return await storeAPI({
        "check-payment-processing": {
            platform: "Web",
            payment_intent_id,
            subscription_id
        }
    })
}

export const updateCustomerEmail = async (email) => {
    return await storeAPI({
        "update-customer-email": {
            platform: "Web",
            email
        }
    })
}

export const getPricingData = async (language = 'en') => {
    return await storeAPI({ get_pricing_data: { language } })
}

export const loadBillingDetails = async (customAuth = null) => {
    const apiInstance = customAuth ? api("store", customAuth) : storeAPI

    const billingResponse = await apiInstance({
        get_billing_details: {
            platform: "Web"
        }
    })

    const hasCompanyName = !!(billingResponse.companyName && billingResponse.companyName.trim())
    const hasTaxId = !!(billingResponse.taxId && billingResponse.taxId.trim())
    const hasAddressLine1 = !!(billingResponse.address?.line1 && billingResponse.address.line1.trim())
    const hasBillingDetails = hasCompanyName || hasTaxId || hasAddressLine1

    if (hasBillingDetails) {
        return {
            needsInvoice: true,
            useSavedBillingDetails: true,
            companyName: billingResponse.companyName,
            taxId: billingResponse.taxId,
            address: billingResponse.address,
            name: billingResponse.name,
            addressComplete: true
        }
    }

    return null
}


export default {
    initializePayment,
    initializePaymentMethodSetup,
    completeSubscriptionAfterSetup,
    getInvoices,
    attachPaymentMethod,
    detachPaymentMethod,
    getPaymentMethods,
    getPromoCodeInfo,
    cancelIncompleteSubscription,
    cancelSubscription,
    getStoreAccount,
    updateCustomerInvoiceDetails,
    validateTaxId,
    getBillingDetails,
    updateBillingDetails,
    checkPaymentProcessing,
    updateCustomerEmail,
    loadBillingDetails,
    addPaymentPreprocessor,
    removePaymentPreprocessor,
    getPricingData
}
/**
 * The composed StoreProduct model: every declared product extension and the
 * enabled ids. Passing `enabled` writes the list (admin).
 */
export const storeProductModel = async (enabled) => await storeAPI({ product_model: enabled ? { enabled } : {} })
